import { css } from '@recruit-robin/robin-theme/web-styles';

/**
 * The apply flow's design tokens, ported from `handover/adecco-apply-flow.css`.
 *
 * They are CSS custom properties rather than a JS object for two reasons: the tenant's
 * brand colour becomes a one-line override on the root, and the values stay readable
 * against the stylesheet they came from. Every property is prefixed `--af-` so nothing
 * here can collide with a global or with robin-theme.
 *
 * Read them through `af`, never as raw strings — the prefix is an implementation detail.
 */
export const af = {
  /** Brand. App bar ground and the exit state. Nothing else — see `docs` below. */
  brand: 'var(--af-brand)',

  /** ZT Talk. Its faces are declared in `fonts.ts`; a role picks one by weight. */
  font: 'var(--af-font)',

  ink: 'var(--af-ink)',
  inkSoft: 'var(--af-ink-soft)',
  muted: 'var(--af-muted)',
  trail: 'var(--af-trail)',
  line: 'var(--af-line)',
  lineSoft: 'var(--af-line-soft)',
  fill: 'var(--af-fill)',
  paper: 'var(--af-paper)',
  stage: 'var(--af-stage)',
  focus: 'var(--af-focus)',

  sp1: 'var(--af-sp-1)',
  sp2: 'var(--af-sp-2)',
  sp3: 'var(--af-sp-3)',
  sp4: 'var(--af-sp-4)',
  sp5: 'var(--af-sp-5)',
  sp6: 'var(--af-sp-6)',
  sp8: 'var(--af-sp-8)',
  sp10: 'var(--af-sp-10)',
  sp12: 'var(--af-sp-12)',

  rCard: 'var(--af-r-card)',
  rBtn: 'var(--af-r-btn)',
  rPill: 'var(--af-r-pill)',
  rSlot: '14px',

  shadowCard: 'var(--af-shadow-card)',
} as const;

/** Default brand colour. Overridden per tenant by `applyFlowThemeCss`. */
export const DEFAULT_BRAND_COLOR = '#EB003F';

/**
 * The container-query name every responsive rule keys off. The query root is the
 * viewport element, not the window — see `ApplyStage`.
 */
export const AF_CONTAINER = 'apply-flow';

/** Material M3 window classes, as container queries against the app root. */
export const compactQuery = `@container ${AF_CONTAINER} (max-width: 599px)`;
export const expandedQuery = `@container ${AF_CONTAINER} (min-width: 600px)`;

/**
 * Gate for every hover rule in the flow.
 *
 * A touch browser applies :hover on tap and keeps it applied until the next tap
 * elsewhere, so an ungated hover state sticks to the option a candidate just answered
 * and rides along to the next screen. Pointer capability, not width — a narrow desktop
 * window still has a mouse, a large tablet does not.
 */
export const hoverQuery = '@media (hover: hover) and (pointer: fine)';

/**
 * Declares every token. Stamped once on the journey root.
 *
 * Only `--af-brand` varies by tenant: the styleguide is explicit that brand colour is the
 * app bar and the exit ground and nothing else, so no other token is derived from it.
 * Interactive emphasis is carried by ink throughout.
 */
export const applyFlowThemeCss = (brandColor: string = DEFAULT_BRAND_COLOR) => css`
  --af-brand: ${brandColor};

  --af-font: 'ZT Talk', sans-serif;

  --af-ink: #111111;
  --af-ink-soft: #4f4f4f;
  --af-muted: #8a8a8a;
  --af-trail: #555555;
  --af-line: #cfcfcf;
  --af-line-soft: #e4e4e4;
  --af-fill: #ededed;
  --af-paper: #ffffff;
  --af-stage: #f6f6f6;
  --af-focus: #111111;

  --af-sp-1: 4px;
  --af-sp-2: 8px;
  --af-sp-3: 12px;
  --af-sp-4: 16px;
  --af-sp-5: 20px;
  --af-sp-6: 24px;
  --af-sp-8: 32px;
  --af-sp-10: 40px;
  --af-sp-12: 48px;

  --af-r-card: 20px;
  --af-r-btn: 22px;
  --af-r-pill: 999px;

  --af-shadow-card: 0 1px 2px rgba(17, 17, 17, 0.05), 0 6px 20px rgba(17, 17, 17, 0.06);
`;

/**
 * Selection, everywhere it appears. An inset ring rather than a border so geometry
 * cannot shift by a pixel on tap, and ink rather than the brand colour so selection can
 * never be confused with branding.
 */
export const selectedRingCss = css`
  box-shadow: inset 0 0 0 2px ${af.ink};
  background: ${af.paper};
`;

/** Surfaces float on shadow, not borders. */
export const floatingSurfaceCss = css`
  border: none;
  border-radius: ${af.rCard};
  background: ${af.paper};
  box-shadow: ${af.shadowCard};
`;

/** Purple, so a keyboard ring can never be read as selection (ink) or brand. */
export const focusRingCss = css`
  &:focus-visible {
    outline: 3px solid ${af.focus};
    outline-offset: 2px;
  }
`;

/** Strips the browser's button chrome without repeating six declarations per component. */
export const bareButtonCss = css`
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
`;

/**
 * The escape hatch under a primary button — "pick another time", "back to my interview".
 *
 * Never a second CTA: every screen in the flow has exactly one of those, and a second filled
 * button beside it would make the candidate choose between two things that look equally
 * intended. Underlined text says "this goes back" without competing.
 */
export const backLinkCss = css`
  ${bareButtonCss};
  padding: 6px 8px;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${af.ink};
  }
`;

/**
 * Puts the theme `DatePicker`'s text on the kit's scale. Interpolate it into the element that
 * directly wraps the picker.
 *
 * The picker takes no class names or props for its type, so this follows its structure: a
 * header (the month menu, then the arrows) over a body (the weekday row, then the month grid).
 * A robin-theme release that reshapes the picker drops these rules silently — the picker keeps
 * working, on its own sizes.
 *
 * The days' colours come from `ApplyDatePickerTheme`, which the picker is wrapped in as well. The
 * selected day's weight stays the picker's: available, unavailable and selected days differ only
 * by classes this side cannot see, so restyling one from here would either miss it or erase the
 * difference.
 */
export const datePickerTypeCss = css`
  /* The month: Small in Bold, the kit's "Bold for slots and months". */
  & > div > div:first-of-type [tabindex] > div > div {
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    letter-spacing: 0;
  }

  /* The weekday initials: Label, in Black capitals like every other capital. */
  & > div > div:nth-of-type(2) > div:first-of-type > div > div {
    font-size: 11px;
    line-height: 16px;
    font-weight: 900;
    letter-spacing: 0.09em;
  }

  /* The days: Small. Their numbers are bare text, so setting the grid reaches every one. */
  & > div > div:nth-of-type(2) > div:nth-of-type(2) {
    font-size: 14px;
    line-height: 20px;
  }
`;
